import React, { useCallback, useEffect, useState } from "react";
import { Clock, RefreshCw, BookOpen } from "lucide-react";
import { getOngoingPeriods } from "../../api/timetable/timetableApi";
import { getOngoingPeriodsPortal } from "../../api/timetable/timetablePortalApi";
import { useTimetableLive } from "../../hooks/useTimetableLive";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  EmptyState,
  Skeleton,
  Alert,
} from "../ui";
import { useToast } from "../../context/ToastContext";

/**
 * OngoingClassesPanel — classes in progress right now.
 * - Initial load: GET /timetable/ongoing (or portal)
 * - Live updates: Socket.IO `timetable:ongoing` (server interval + attendance/CRUD pushes)
 */
const ongoingColumns = [
  {
    key: "periodNumber",
    header: "Period",
    render: (v) => <span className="font-bold text-neutral-900 dark:text-neutral-100">P{v.periodNumber}</span>,
  },
  {
    key: "time",
    header: "Time",
    render: (v) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400">
        {v.startTime} - {v.endTime}
      </span>
    ),
  },
  {
    key: "classSection",
    header: "Class & Section",
    render: (v) => (
      <span className="font-medium text-neutral-900 dark:text-neutral-100">
        {v.classId?.className || "—"} - {v.sectionId?.sectionName || "—"}
      </span>
    ),
  },
  {
    key: "subject",
    header: "Subject",
    render: (v) => v.subjectId?.name || "N/A",
  },
  {
    key: "faculty",
    header: "Faculty",
    render: (v) => (
      <span className="font-semibold text-primary-800 dark:text-primary-300">
        {v.staffId?.name || "—"}
        {v.staffId?.staffCode ? (
          <span className="ml-1 text-xs font-normal text-neutral-500 dark:text-neutral-400">
            ({v.staffId.staffCode})
          </span>
        ) : null}
      </span>
    ),
  },
];

/**
 * OngoingBadge — small pill with a pulsating dot, shown whenever there
 * are classes currently in progress. The dot only animates while there's
 * something actually ongoing, so it doubles as a status signal rather
 * than pure decoration.
 */
function OngoingBadge({ count }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 dark:bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-600/20 dark:ring-green-400/20">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
      </span>
      {count} {count === 1 ? "class" : "classes"} live
    </span>
  );
}

export default function OngoingClassesPanel({ isPortal = false, autoFetch = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [live, setLive] = useState(false);

  const toast = useToast();
  const [error, setError] = useState(null);

  const fetchOngoing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = isPortal ? await getOngoingPeriodsPortal() : await getOngoingPeriods();
      const list = Array.isArray(res) ? res : res?.data || [];
      setRows(list);
      setLastFetchedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch ongoing classes");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    if (autoFetch) fetchOngoing();
  }, [autoFetch, fetchOngoing]);

  useTimetableLive({
    isPortal,
    enabled: true,
    onOngoing: (list) => {
      setRows(list);
      setLastFetchedAt(new Date());
      setLive(true);
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary-700 dark:text-primary-400 shrink-0" />
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold">Ongoing Classes</CardTitle>
              <OngoingBadge count={rows.length} />
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Periods in session now (teacher not marked absent)
              {live ? " · Live" : null}
              {lastFetchedAt
                ? ` · Updated ${lastFetchedAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : null}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          onClick={fetchOngoing}
          isLoading={loading}
          leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
        >
          Refresh
        </Button>
      </CardHeader>

      {error ? (
        <CardContent className="pt-0">
          <Alert variant="danger" title="Error">
            {error}
          </Alert>
        </CardContent>
      ) : null}

      <CardContent className="p-0">
        {loading && rows.length === 0 ? (
          <div className="p-6 space-y-3">
            <Skeleton height={20} width="40%" />
            <Skeleton height={120} width="100%" />
          </div>
        ) : (
          <Table
            columns={ongoingColumns}
            data={rows}
            emptyState={
              <EmptyState
                icon={BookOpen}
                title="No classes in progress right now"
                description="When the current time falls inside a scheduled teaching period and the assigned teacher is not absent, rows appear here automatically."
              />
            }
          />
        )}
      </CardContent>
    </Card>
  );
}